import { DomainError } from "./errors";
import type { PlannedTrialId } from "./lib/planned-trial";
import type { MemberId } from "./member-identity";
import type { ObservationId } from "./observation.model";
import type { QuestionnaireInstanceId } from "./questionnaire-instance.model";
import type { AnsweredValue, ParameterValue } from "./value.model";

// ============================================================
// Domain errors for the redesigned catalog / instance models
//
// Kept apart from the operational `errors.ts` for the same reason the
// models are: this is a parallel implementation, and sharing an error type
// with the code being replaced would make the two impossible to retire
// separately. Same idiom throughout — a DomainError subclass per failure
// KIND, a details bag carrying the identifiers a caller needs to point at
// the offending thing, and a message a human can read without the details.
// ============================================================

/**
 * The identifiers a catalog failure can name. All optional: an error raised
 * inside a value object knows the member it hangs off but not always the
 * lineage, and one raised in a constructor knows the lineage but has no
 * instance to ask for anything else.
 */
interface CatalogErrorDetails {
  /** Lineage identity — the canonical url, never a database id. */
  url?: string;
  version?: number;
  name?: string;
  /** System-assigned member identity, where the failure is about one member. */
  // Raw input is retained when asMemberId rejects an invalid stored identity.
  memberId?: string;
  /** The finding lineage involved in the failure, addressed by canonical url. */
  findingUrl?: string;
  /** Which collection the member lives in, for a message that can locate it. */
  collection?: string;
  field?: string;
}

/** Structural or content validation failed. A definition that exists is valid, so this refuses construction. */
export class InvalidCatalogDefinitionError extends DomainError<"CATALOG_DEFINITION_INVALID", CatalogErrorDetails> {
  constructor(message: string, details: CatalogErrorDetails = {}) {
    super("InvalidCatalogDefinitionError", "CATALOG_DEFINITION_INVALID", message, details);
  }
}

/**
 * A revision was refused: stale, unchanged, or claiming a `(url, version)`
 * pair a sibling instance already holds. Separate from the invalid-content
 * error because the content may be perfectly well formed — what failed is the
 * lineage's linear history, which no amount of editing the payload fixes.
 */
export class CatalogRevisionError extends DomainError<"CATALOG_REVISION_INVALID", CatalogErrorDetails> {
  constructor(message: string, details: CatalogErrorDetails = {}) {
    super("CatalogRevisionError", "CATALOG_REVISION_INVALID", message, details);
  }
}

/** One stored value a save would leave invalid, located so the editor can offer to clear it. */
export interface InvalidatedValue {
  message: string;
  occurrenceId: MemberId;
  plannedTrialId?: PlannedTrialId;
  source: "self" | MemberId;
  parameterId: MemberId;
  value: AnsweredValue;
  /** The parameter as declared across its revisions, so a removed option still has a name. */
  declaration?: ParameterValue;
  procedure: string;
  /** 1-based planned trial; absent for the occurrence's own answer. */
  trial?: number;
  /** From the current revision, else the last one that declared it. */
  parameterLabel?: string;
  reason: "parameter-removed" | "option-removed" | "invalid";
  detail?: string;
}

export interface InvalidatedProtocol {
  url: string;
  name: string;
  /** The current revision, which a fix revises from. */
  version: number;
  problems: readonly InvalidatedValue[];
}

/**
 * A catalog save would leave active protocols holding parameter values the
 * current procedure revisions no longer accept. Protocols cite procedures
 * unversioned, so the values have to stay valid against whatever is current.
 */
export class ProtocolsInvalidatedError extends DomainError<
  "CATALOG_PROTOCOLS_INVALIDATED",
  { protocols: readonly InvalidatedProtocol[] }
> {
  constructor(protocols: readonly InvalidatedProtocol[]) {
    super(
      "ProtocolsInvalidatedError",
      "CATALOG_PROTOCOLS_INVALIDATED",
      `Protocols hold values this would invalidate: ${protocols.map((protocol) => protocol.name).join(", ")}`,
      { protocols },
    );
  }
}

/**
 * An availability command was refused. Only activation can fail — deactivation
 * is never blocked, whatever references the definition, because withdrawing
 * something unsafe must not wait on revising every dependent.
 */
export class CatalogAvailabilityError extends DomainError<"CATALOG_AVAILABILITY_INVALID", CatalogErrorDetails> {
  constructor(message: string, details: CatalogErrorDetails = {}) {
    super("CatalogAvailabilityError", "CATALOG_AVAILABILITY_INVALID", message, details);
  }
}

/**
 * A setter was handed a member carrying an id the aggregate does not
 * recognise. Not an invitation to create one: an unknown id is either a stale
 * client or a typo, and minting for it would produce a member whose stored
 * references point nowhere.
 */
export class UnknownMemberError extends DomainError<"CATALOG_MEMBER_UNKNOWN", CatalogErrorDetails> {
  constructor(message: string, details: CatalogErrorDetails) {
    super("UnknownMemberError", "CATALOG_MEMBER_UNKNOWN", message, details);
  }
}

/**
 * A value object's own rules were broken — min above max, a choice with no
 * options, a blank unit, a step that does not divide the range. Its own error
 * because a value object is validated in every model that composes it, and the
 * caller catching it wants to know it was the value and not the member around it.
 */
export class InvalidValueDefinitionError extends DomainError<"CATALOG_VALUE_INVALID", CatalogErrorDetails> {
  constructor(message: string, details: CatalogErrorDetails = {}) {
    super("InvalidValueDefinitionError", "CATALOG_VALUE_INVALID", message, details);
  }
}

interface InstanceErrorDetails {
  /** The instance's own id, where it has been persisted. */
  instanceId?: QuestionnaireInstanceId;
  /** The definition revision the instance is pinned to. */
  definitionUrl?: string;
  definitionVersion?: number;
  /** System-assigned member identity — an option, which the finding aggregate mints. */
  memberId?: MemberId | ObservationId;
  /** The finding lineage involved in the failure, addressed by canonical url. */
  findingUrl?: string;
  status?: string;
  field?: string;
}

/** An instance command was refused on its own content — an answer that does not fit its definition's value object. */
export class InvalidInstanceError extends DomainError<"INSTANCE_INVALID", InstanceErrorDetails> {
  constructor(message: string, details: InstanceErrorDetails = {}) {
    super("InvalidInstanceError", "INSTANCE_INVALID", message, details);
  }
}

/**
 * An instance command was refused on its status. Its own error because the
 * caller's remedy differs: invalid content is fixed by sending different
 * content, a wrong status is fixed by reopening or by not sending at all.
 */
export class InvalidInstanceTransitionError extends DomainError<"INSTANCE_INVALID_TRANSITION", InstanceErrorDetails> {
  constructor(message: string, details: InstanceErrorDetails = {}) {
    super("InvalidInstanceTransitionError", "INSTANCE_INVALID_TRANSITION", message, details);
  }
}

interface OrderSetErrorDetails {
  // A malformed RequestGroup may be reported by its raw row id before hydration.
  orderSetId?: string;
  // Codec failures can identify an unvalidated ServiceRequest row before hydration.
  orderId?: string;
  /** The exam or procedure definition lineage the failure is about. */
  url?: string;
  step?: number;
  parameterId?: MemberId;
}

/** A curation command was refused — an unknown order, an exam whose declared diagnostic is not in the timed work. */
export class InvalidOrderSetError extends DomainError<"ORDER_SET_INVALID", OrderSetErrorDetails> {
  constructor(message: string, details: OrderSetErrorDetails = {}) {
    super("InvalidOrderSetError", "ORDER_SET_INVALID", message, details);
  }
}
